// @flow
import _ from 'lodash'
import React, { Component } from 'react'
import Select from '../controls/Select'
import type { ActionType, OptionType } from '../types'
import styled from 'styled-components'
import ErrorText from '../controls/ErrorText'
import { BoldText } from '../AutomatedRulesTable/AutomatedRulesTableRow'
import matchesNotFloat from '../controls/FloatValueMatcher'

const ActionInputWrapper = styled.div`
  display: table;
  width: ${({ withAmount }: Object) => (withAmount ? '67%' : '35%')};
  padding-bottom: 10px;
`

const AmountInputWrapper = styled.div`
  display: table-cell;
  width: 30%;
  vertical-align: middle;
`

const AmountPrefix = styled.div`
  display: table-cell;
  width: 1%;
  vertical-align: middle;
  padding-left: 5px;
`

const Amount = styled.div`
  padding: 0 5px;
  display: table-cell;
  width: 5%;
  vertical-align: middle;
`

const AmountType = styled.div`
  display: table-cell;
  width: 3%;
  vertical-align: middle;
`

const EdgeAmountPrefix = styled.div`
  display: table-cell;
  width: 12%;
  vertical-align: middle;
  padding-left: 5px;
`

const EdgeAmount = styled.div`
  padding: 0 5px;
  display: table-cell;
  width: 5%;
  vertical-align: middle;
`

const SelectWrapper = styled.div`
  padding: 10px 0;
  width: 35%;
`

const CellSelectWrapper = styled.div`
  display: table-cell;
  width: 35%;
  vertical-align: middle;
`

const SelectHeader = styled.div`
  font-size: 14px;
  padding-bottom: 5px;
`

type Props = {|
  t: Function,
  action: ActionType,
  options: Array<OptionType>,
  isOnlyInfo?: boolean,
  disabled?: boolean,
  actionErrors?: Object,
  onChange?: Function
|}

const typesWithAmount = ['increase_price', 'decrease_price', 'increase_budget', 'decrease_budget']
const amountTypeOptions = [
  { id: 'percent', name: '%' },
  { id: 'sum', name: '₽' }
]

export default class Action extends Component<Props> {
  render() {
    const { t, isOnlyInfo, action, options, disabled, actionErrors } = this.props

    if (isOnlyInfo) {
      return (
        <div>
          <BoldText>{t('action')}</BoldText>
          <div>{this.shortDescription()}</div>
        </div>
      )
    }

    return (
      <div>
        <SelectHeader>{t('header.action')}</SelectHeader>
        <ActionInputWrapper
          className="t-ActionInput__action-type-select"
          withAmount={this.typeWithAmount()}
        >
          <CellSelectWrapper>
            <Select
              placeholder={t('select_action')}
              value={action.action_type}
              options={options}
              disabled={disabled}
              onChange={(val) => this.typeChanged(val)}
            />
          </CellSelectWrapper>
          {this.typeWithAmount() && (
            <AmountInputWrapper>
              <AmountPrefix>{t('amount_prefix')}</AmountPrefix>
              <Amount>
                <input
                  className="input-item input-item_full-width"
                  value={action.amount || ''}
                  disabled={disabled}
                  onChange={(e) => this.amountChanged(e.target.value)}
                />
              </Amount>
              <AmountType>
                <Select
                  value={action.amount_type || 'sum'}
                  options={amountTypeOptions}
                  disabled={disabled}
                  onChange={(val) => this.amountTypeChanged(val)}
                />
              </AmountType>
              <EdgeAmountPrefix>{t('edge_amount_prefix')}</EdgeAmountPrefix>
              <EdgeAmount>
                <input
                  className="input-item input-item_full-width"
                  value={action.edge_amount || ''}
                  disabled={disabled}
                  onChange={(e) => this.edgeAmountChanged(e.target.value)}
                />
              </EdgeAmount>
            </AmountInputWrapper>
          )}
        </ActionInputWrapper>
        {actionErrors && <ErrorText errors={actionErrors.action_type} />}
        {actionErrors && <ErrorText errors={actionErrors.amount} />}
        {actionErrors && <ErrorText errors={actionErrors.edge_amount} />}
        <SelectWrapper>
          <SelectHeader>{t('select_apply_frequency')}</SelectHeader>
          <Select
            value={action.apply_frequency || 'once_per_hour'}
            options={this.applyFrequencyOptions()}
            disabled={disabled}
            onChange={(val) => this.applyFrequencyChanged(val)}
          />
          {actionErrors && actionErrors.apply_frequency && (
            <ErrorText errors={actionErrors.apply_frequency} />
          )}
        </SelectWrapper>
      </div>
    )
  }

  typeWithAmount(): boolean {
    return _.includes(typesWithAmount, this.props.action.action_type)
  }

  amountChanged(amount: string) {
    const { onChange, action } = this.props
    if (matchesNotFloat(amount)) return
    onChange && onChange(_.extend({}, action, { amount }))
  }

  edgeAmountChanged(edgeAmount: string) {
    const { onChange, action } = this.props
    if (matchesNotFloat(edgeAmount)) return
    onChange && onChange(_.extend({}, action, { edge_amount: edgeAmount }))
  }

  amountTypeChanged(amountType: string) {
    const { onChange, action } = this.props
    onChange && onChange(_.extend({}, action, { amount_type: amountType }))
  }

  typeChanged(actionType: string) {
    const { onChange, action } = this.props
    onChange && onChange(_.extend({}, action, { action_type: actionType }))
  }

  applyFrequencyChanged(frequency: string) {
    const { onChange, action } = this.props
    onChange && onChange(_.extend({}, action, { apply_frequency: frequency }))
  }

  shortDescription() {
    const { t, action } = this.props
    const { amount } = action
    const actionType = action.action_type
    const amountType = action.amount_type
    const edgeAmount = action.edge_amount
    if (this.typeWithAmount()) {
      let amountString = _.join([t('amount_prefix'), _.join([amount, t(amountType)], '')], ' ')
      let edgeAmountString = edgeAmount
        ? _.join([t('edge_amount_prefix'), _.join([edgeAmount, t('sum')], '')], ' ')
        : ''
      return _.join([t(actionType), amountString, edgeAmountString], ' ')
    } else {
      return t(actionType)
    }
  }

  applyFrequencyOptions() {
    const { t } = this.props
    return [
      { id: 'once_per_hour', name: t('once_per_hour') },
      { id: 'once_per_two_hours', name: t('once_per_two_hours') },
      { id: 'once_per_three_hours', name: t('once_per_three_hours') },
      { id: 'once_per_four_hours', name: t('once_per_four_hours') },
      { id: 'once_per_day', name: t('once_per_day') },
      { id: 'once_per_week', name: t('once_per_week') },
      { id: 'once_per_two_weeks', name: t('once_per_two_weeks') }
    ]
  }
}
